import { Currency } from '@ps/web-core-enums';
import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoJackpot } from '@ps/web-martech-cms-types';
import { takeUntil } from 'rxjs/operators';

import { transformParams } from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig } from '@swf/core/models/casino/utils';
import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoGame } from '@swf/core/types/CasinoGame';
import { currencyChange, logout, pageChange } from '@swf/core/utils/actions';

import { WidgetVariant } from './CasinoJackpotWidget';

const initialState: CasinoJackpotWidgetState = {
  key: null,
  winUpTo: 0,
  wonToday: 0,
  paidToday: 0,
  jackpotKing: { value: 0, currency: Currency.USDollar, key: '', fixedAmount: 0 },
  regalPot: { value: 0, currency: Currency.USDollar, key: '', fixedAmount: 0 },
  royalPot: { value: 0, currency: Currency.USDollar, key: '', fixedAmount: 0 },
  currency: Currency.Euro,
  gameConfigs: [],
  loading: true
};

export interface CasinoJackpotWidgetState {
  key: string | null;
  winUpTo?: number;
  wonToday?: number;
  paidToday?: number;
  jackpotKing?: { value: number; currency: Currency; key: string; fixedAmount: number };
  regalPot?: { value: number; currency: Currency; key: string; fixedAmount: number };
  royalPot?: { value: number; currency: Currency; key: string; fixedAmount: number };
  currency: Currency;
  gameConfigs: CasinoGame[];
  loading: boolean;
}

const reducers = {
  set: (state: CasinoJackpotWidgetState, payload: CasinoJackpotWidgetState) => ({
    ...state,
    ...payload
  })
};

export const effects = {
  async preload(
    payload: undefined,
    { actions, dispatch, widget: { uid } }: WidgetEffectParams<CasinoJackpot>
  ) {
    const { fetch }: ExtractDispatchFromModel<CasinoJackpotWidgetModel> = dispatch[uid];
    pageChange(actions)?.subscribe(() => fetch());

    await fetch();
  },
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoJackpot>) {
    const { actions, dispatch, select, request, requestStream, rootState, widget } = params;
    const isHTTP = widget.widget_variant === WidgetVariant.hunt;

    const { uid, game_service: service } = widget;
    const widgetDispatch: ExtractDispatchFromModel<CasinoJackpotWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoJackpotWidgetModel> = select[uid];
    const optionsCms = select.cms.getRequestOptions(rootState);
    const options = { ...optionsCms, service, body: transformParams(widget.params) };

    if (
      !areGamesVisibleByConfig({
        params,
        service,
        hasState: widgetSelect.get(rootState)?.gameConfigs.length > 0
      })
    ) {
      return;
    }

    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;
    widgetDispatch.set({ ...(widgetSelect.get(rootState) ?? initialState), loading: true });

    if (!canUseDOM || isHTTP) {
      try {
        const { body: data } = await request<CasinoJackpotWidgetState>({
          ...options,
          ...(isHTTP ? { http: true } : {})
        });
        widgetDispatch.set({ ...(data ? data : initialState), loading: false });
      } catch {
        widgetDispatch.set({ ...initialState, loading: false });
      }
    } else {
      requestStream<CasinoJackpotWidgetState>(
        {
          ...options
        },
        undefined
      )
        .pipe(takeUntil(currencyChange(actions) as typeof actions), takeUntil(onLogout))
        .subscribe((update) => widgetDispatch.set({ ...(update ?? initialState), loading: false }));
    }
  }
};

const selectors = (slice: Slicer<CasinoJackpotWidgetState>) => ({
  get() {
    return slice;
  }
});

export interface CasinoJackpotWidgetModel
  extends WidgetModelConfig<CasinoJackpot, CasinoJackpotWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const jackpotWidgetModel: CasinoJackpotWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const useJackpotWidgetModel: UseWidget<CasinoJackpot, CasinoJackpotWidgetModel> = useWidget;
